import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import {
    SovosBox,
    SovosMenuItem,
    SovosToolbar,
    SovosToolbarActions,
    SovosToolbarMoreMenu,
    SovosTypography,
} from "mosaic-react";
import { OpenInNew, PlayArrow } from "mosaic-react-icons";
import {
    getImportProfileMappingByProfileAction,
    runCustomTemplateMappingAction,
    suggestCustomTemplateFiscalYearAction,
} from "../../../store/actions/userActions";
import ProceedFiscalYearDialog from "../../../store/views/MappingFlow/ColumnMappingCanvas/components/ProceedFiscalYearDialog";
import ImportCompleteView from "../../../store/views/MappingFlow/ImportCompleteView";
import {
    buildFiscalYearOptions,
    getSuggestedFiscalYear,
    resolveProceedImportId,
} from "../../../store/views/MappingFlow/nextImportProfile";
import { trackUserAction } from "../../../utils/pendo";

const findOrganizationAndProject = (fetchResults, projectId) => {
    const candidates = [];
    Object.values(fetchResults || {}).forEach((value) => {
        if (!value || typeof value !== "object") return;
        if (Array.isArray(value.projects)) candidates.push(value);
        if (Array.isArray(value.list)) {
            value.list.forEach((item) => {
                if (item && Array.isArray(item.projects)) candidates.push(item);
            });
        }
    });
    for (const organization of candidates) {
        const project = organization.projects.find((item) => String(item.id) === String(projectId));
        if (project) {
            return { organization, project };
        }
    }
    return { organization: null, project: null };
};

export default function Goto(props) {
    const { i18n, history, params, pushNotification, modules, fetch } = useGlobalValue();
    const dispatch = useDispatch();
    const { value: cell } = props || {};
    const { id, entity, mapping_id, has_mapping } = cell || {};

    const [fiscalYearDialogOpen, setFiscalYearDialogOpen] = useState(false);
    const [aiFiscalYear, setAiFiscalYear] = useState("");
    const [aiFiscalYearScore, setAiFiscalYearScore] = useState(null);
    const [aiFiscalYearRationale, setAiFiscalYearRationale] = useState("");
    const [aiFiscalYearLoading, setAiFiscalYearLoading] = useState(false);
    const [proceeding, setProceeding] = useState(false);
    const [importComplete, setImportComplete] = useState(false);
    const fiscalYearSuggestSeq = useRef(0);

    const { organization, project } = useMemo(
        () => findOrganizationAndProject(fetch?.results, params?.projectId),
        [fetch?.results, params?.projectId],
    );
    const fiscalYearOptions = useMemo(
        () => buildFiscalYearOptions(project?.fiscalYears, params?.fiscalYear),
        [project?.fiscalYears, params?.fiscalYear],
    );
    const hasAiSuggestions = Array.isArray(modules) && modules.includes("ai_suggestions");
    const suggestedFiscalYear =
        aiFiscalYear || getSuggestedFiscalYear(fiscalYearOptions, params?.fiscalYear, false);
    const profileId = parseInt(id, 10);

    const handleOpen = () => {
        const locale = params?.locale || "en";
        const projectId = params?.projectId;
        const fiscalYear = params?.fiscalYear;
        const path = `/${locale}/${projectId}/${fiscalYear}/${id}/column_mapping/${entity || ""}`;
        history.push(path);
    };

    const notify = (message, type = "error") => {
        if (pushNotification) {
            pushNotification({ open: true, message, type, timeout: "short" });
        }
    };

    const handleRunMapping = () => {
        setAiFiscalYear("");
        setAiFiscalYearScore(null);
        setAiFiscalYearRationale("");
        setFiscalYearDialogOpen(true);
        if (!hasAiSuggestions || !profileId) {
            setAiFiscalYearLoading(false);
            return;
        }
        const seq = ++fiscalYearSuggestSeq.current;
        setAiFiscalYearLoading(true);
        dispatch(
            suggestCustomTemplateFiscalYearAction(
                profileId,
                fiscalYearOptions,
                organization?.id?.toString() || "",
            ),
        )
            .then((result) => {
                if (seq !== fiscalYearSuggestSeq.current) return;
                if (!result?.fiscal_year) return;
                setAiFiscalYear(String(result.fiscal_year));
                setAiFiscalYearScore(typeof result.score === "number" ? result.score : null);
                setAiFiscalYearRationale(
                    typeof result.rationale === "string" ? result.rationale : "",
                );
            })
            .finally(() => {
                if (seq !== fiscalYearSuggestSeq.current) return;
                setAiFiscalYearLoading(false);
            });
    };

    const handleRunCancel = () => {
        if (proceeding) return;
        fiscalYearSuggestSeq.current += 1;
        setAiFiscalYearLoading(false);
        setFiscalYearDialogOpen(false);
        setAiFiscalYear("");
        setAiFiscalYearScore(null);
        setAiFiscalYearRationale("");
    };

    const handleRunConfirm = async (selectedFiscalYear) => {
        if (proceeding || !profileId || !params?.projectId || !selectedFiscalYear) return;
        setProceeding(true);
        try {
            let resolvedMappingId = parseInt(mapping_id, 10);
            if (!resolvedMappingId) {
                const mapping = await dispatch(getImportProfileMappingByProfileAction(profileId));
                resolvedMappingId = mapping?.id;
            }
            if (!resolvedMappingId) {
                notify(i18n`No mapping found for this profile`);
                setFiscalYearDialogOpen(false);
                return;
            }
            const dataFileImportId = resolveProceedImportId(
                project?.fiscalYears?.[selectedFiscalYear],
            );
            trackUserAction("mapping_run_started", {
                profile_id: profileId,
                mapping_id: resolvedMappingId,
                fiscal_year: String(selectedFiscalYear),
                import_id: dataFileImportId,
            });
            const result = await dispatch(
                runCustomTemplateMappingAction({
                    profileId,
                    mappingId: resolvedMappingId,
                    projectId: params?.projectId,
                    fiscalYear: String(selectedFiscalYear),
                    importId: dataFileImportId,
                }),
            );
            if (result?.success) {
                setFiscalYearDialogOpen(false);
                setAiFiscalYear("");
                setAiFiscalYearScore(null);
                setAiFiscalYearRationale("");
                setImportComplete(true);
                return;
            }
            notify(i18n`${result?.message || "Failed to run mapping"}`);
        } catch (_error) {
            notify(i18n`Unexpected error running mapping`);
        } finally {
            setProceeding(false);
        }
    };

    useEffect(
        () => () => {
            fiscalYearSuggestSeq.current += 1;
        },
        [],
    );

    return (
        <SovosBox onClick={(e) => e?.stopPropagation()}>
            <SovosToolbar sx={{ backgroundColor: "transparent !important" }}>
                <SovosToolbarActions>
                    <SovosToolbarMoreMenu>
                        <SovosMenuItem onClick={handleOpen}>
                            <OpenInNew />
                            <SovosTypography children={i18n`Edit Mapping`} variant={"span"} />
                        </SovosMenuItem>
                        {has_mapping && (
                            <SovosMenuItem
                                onClick={(e) => {
                                    e?.stopPropagation();
                                    handleRunMapping();
                                }}
                            >
                                <PlayArrow />
                                <SovosTypography children={i18n`Run Mapping`} variant={"span"} />
                            </SovosMenuItem>
                        )}
                    </SovosToolbarMoreMenu>
                </SovosToolbarActions>
            </SovosToolbar>
            <ProceedFiscalYearDialog
                open={fiscalYearDialogOpen}
                fiscalYears={fiscalYearOptions}
                suggestedFiscalYear={suggestedFiscalYear}
                suggestedScore={aiFiscalYearScore}
                suggestedRationale={aiFiscalYearRationale}
                suggesting={aiFiscalYearLoading}
                confirming={proceeding}
                i18n={i18n}
                onCancel={handleRunCancel}
                onConfirm={handleRunConfirm}
            />
            <ImportCompleteView
                open={importComplete}
                i18n={i18n}
                onClose={() => setImportComplete(false)}
            />
        </SovosBox>
    );
}
